import { isFinitePin } from 'library/helpers/floorplanFrame';
import SuperFetch from 'library/helpers/superFetch';
import {
	buildOverrideToast,
	describeUnlockError,
	overrideRowFromResponse,
	UNLOCK_UNREACHABLE_MESSAGE
} from 'library/helpers/unlockError';
import { all, call, put, select, takeEvery, takeLatest } from 'redux-saga/effects';
import { Notification } from 'zComponents/atoms';
import { accessLogRefreshAction } from '../accessLogs/refresh';
import { accessLogsActions } from '../accessLogs/slice';
import { refreshHeldActivity, watchActivity } from '../_shared/activity';
import { credentialsActions } from '../credentials/slice';
import { selectDevices } from './selectors';
import { devicesActions } from './slice';

// Fetches devices for the active scope (orgID + siteID from selectedProfile)
// Payload can override siteID — used by Lockers to force 'all' bucket
export function* getDevices({ payload } = {}) {
	const { orgID, siteID: profileSiteID } = yield select((state) => state.Auth.selectedProfile);
	const siteID = payload?.siteID ?? profileSiteID;
	const query = { includeFields: true, ...(siteID !== 'all' && { siteID }) };

	// Set loading on target bucket (no saga listens to loadDevicesStart — no loop)
	yield put(devicesActions.loadDevicesStart({ orgID, siteID }));

	try {
		const devices = yield call(() => SuperFetch.get('/devices', {}, query));

		if (devices.statusCode === 200) {
			yield put(devicesActions.loadDevicesSuccess({ orgID, siteID, data: devices.data }));
		} else {
			throw Error(devices.error.message || 'Failed to load devices.');
		}
	} catch (error) {
		yield put(devicesActions.loadDevicesError({ orgID, siteID, error: error.message }));
	}
}

export function* getDevicesSuccess() {
	yield put(devicesActions.loadAllDevicesLocations());
}

// Patches location + sites onto devices in the active scope bucket
export function* getAllDevicesLocations() {
	try {
		const { orgID, siteID } = yield select((state) => state.Auth.selectedProfile);
		const query = siteID !== 'all' ? { siteID } : {};
		const devices = yield call(() => SuperFetch.get('/devices/getAllDevicesAllLocations', {}, query));
		if (devices.statusCode === 200) {
			yield put(devicesActions.loadAllDevicesLocationsSuccess({ orgID, siteID, data: devices.data }));
		} else {
			throw Error(devices.error.message || 'Failed to load devices locations.');
		}
	} catch (error) {
		yield put(devicesActions.loadAllDevicesLocationsError(error));
	}
}

// REFRESH DEVICES (silent — no loading spinner, used after CRUD operations)
export function* refreshDevices({ payload } = {}) {
	try {
		const { orgID, siteID: profileSiteID } = yield select((state) => state.Auth.selectedProfile);
		const siteID = payload?.siteID ?? profileSiteID;
		const query = { includeFields: true, ...(siteID !== 'all' && { siteID }) };
		const devices = yield call(() => SuperFetch.get('/devices', {}, query));

		if (devices.statusCode === 200) {
			yield put(devicesActions.refreshDevicesSuccess({ orgID, siteID, data: devices.data }));
		}
	} catch (error) {
		// Silent refresh — don't show error notifications
	}
}

export function* createDevice({ payload }) {
	try {
		const { fields, categoryID, name, floor: floorID, mapID, coordinates } = payload;

		const fieldsData = fields
			? fields
					.filter((field) => field.fieldID && (field.value || field.value === 0))
					.map((field) => ({
						fieldID: field.fieldID,
						value: field.value
					}))
			: [];

		const device = yield call(() => SuperFetch.post('/devices', { categoryID, name }));
		if (device.statusCode === 201 || device.statusCode === 200) {
			if (fieldsData.length > 0) {
				yield call(createDeviceField, { payload: { deviceID: device.data, data: { fields: fieldsData } } });
			}
			// isFinitePin, not `!= null`: NaN passes `!= null` and reaches the API as null.
			if (isFinitePin(coordinates)) {
				yield call(createDeviceLocation, {
					payload: {
						deviceID: device.data,
						data: { xAxis: coordinates.x, yAxis: coordinates.y, mapID, floorID }
					}
				});
			}
			Notification('success', 'Device created', 'Device added successfully.');

			yield put(devicesActions.createDeviceSuccess());
		} else {
			Notification('error', 'Device failed', 'Failed to add device.');
			throw Error(device.error.message || 'Failed to create device.');
		}
	} catch (error) {
		yield put(devicesActions.createDeviceError(error));
	}
}

export function* createMultipleDevices({ payload }) {
	try {
		const { devices: deviceCount, name: baseName, ...restPayload } = payload;
		const numberOfDevices = deviceCount || 1;
		let successCount = 0;
		let failureCount = 0;

		for (let i = 1; i <= numberOfDevices; i++) {
			try {
				const deviceName = numberOfDevices > 1 ? `${baseName} ${i}` : baseName;
				const {
					fields,
					categoryID,
					floor: floorID,
					mapID,
					coordinates
				} = {
					...restPayload,
					name: deviceName
				};

				const fieldsData = fields
					? fields
							.filter((field) => field.fieldID && (field.value || field.value === 0))
							.map((field) => ({
								fieldID: field.fieldID,
								value: field.value
							}))
					: [];

				const device = yield call(() => SuperFetch.post('/devices', { categoryID, name: deviceName }));
				if (device.statusCode === 201 || device.statusCode === 200) {
					if (fieldsData.length > 0) {
						try {
							yield call(createDeviceField, {
								payload: { deviceID: device.data, data: { fields: fieldsData } }
							});
						} catch (fieldError) {
							console.error(`Failed to create fields for device ${i}:`, fieldError);
						}
					}
					// isFinitePin, not `!= null`: NaN passes `!= null` and reaches the API as null.
					if (isFinitePin(coordinates)) {
						try {
							yield call(createDeviceLocation, {
								payload: {
									deviceID: device.data,
									data: { xAxis: coordinates.x, yAxis: coordinates.y, mapID, floorID }
								}
							});
						} catch (locationError) {
							console.error(`Failed to create location for device ${i}:`, locationError);
						}
					}
					successCount++;
				} else {
					failureCount++;
					console.error(`Failed to create device ${i}: ${device.error?.message}`);
				}

				// Add a small delay between requests
				if (i < numberOfDevices) {
					yield call(() => new Promise((resolve) => setTimeout(resolve, 200)));
				}
			} catch (error) {
				failureCount++;
				console.error(`Failed to create device ${i}:`, error);
			}
		}

		if (successCount > 0) {
			const message =
				numberOfDevices > 1
					? `Successfully created ${successCount} of ${numberOfDevices} devices.${
							failureCount > 0 ? ` ${failureCount} failed.` : ''
						}`
					: 'Device created successfully.';
			Notification('success', 'Devices created', message);
			yield put(devicesActions.createMultipleDevicesSuccess());
		} else {
			throw new Error('Failed to create any devices.');
		}
	} catch (error) {
		Notification('error', 'Create failed', error.message || 'Failed to create devices.');
		yield put(devicesActions.createMultipleDevicesError(error));
	}
}

//🔥 Actions here!, After adding device
export function* createDeviceSuccess() {
	yield put(devicesActions.refreshDevices());
}

//🔥 Actions here!, After adding multiple devices
export function* createMultipleDevicesSuccess() {
	yield put(devicesActions.refreshDevices());
}

export function* updateDevice({ payload }) {
	try {
		const { deviceID, data } = payload;
		const device = yield call(() => SuperFetch.patch(`/devices/${deviceID}`, data));
		if (device.statusCode === 200) {
			Notification('success', 'Device updated', 'Device name updated successfully.');
			yield put(devicesActions.updateDeviceSuccess({ deviceID }));
		} else {
			throw Error(device.error.message || 'Failed to update device name.');
		}
	} catch (error) {
		yield put(devicesActions.updateDeviceError(error));
	}
}

//🔥 Actions here!, After updating device
export function* updateDeviceSuccess() {
	yield put(devicesActions.refreshDevices());
}

// ✅ Single or bulk delete via /devices/bulk; each device is deleted on its own and the
// { deleted, failed, notFound } summary reports how each one went.
export function* deleteDevice({ payload }) {
	try {
		const { deviceIDs } = payload;

		// Bulk endpoint accepts at most 50 IDs per request
		const chunks = [];
		for (let i = 0; i < deviceIDs.length; i += 50) chunks.push(deviceIDs.slice(i, i + 50));

		const responses = yield all(
			chunks.map((chunk) => call(() => SuperFetch.delete('/devices/bulk', { deviceIDs: chunk })))
		);

		let deleted = 0;
		let failed = 0;
		let notFound = 0;
		responses.forEach((res) => {
			if (![200, 204].includes(res.statusCode)) {
				throw Error(res.error?.message || res.error || 'Failed to delete device(s).');
			}
			const summary = res.data?.summary || res.summary || {};
			deleted += summary.deleted || 0;
			failed += summary.failed || 0;
			notFound += summary.notFound || 0;
		});

		if (deleted === 0 && failed > 0) {
			throw Error(`Failed to delete ${failed} device${failed > 1 ? 's' : ''}.`);
		}

		yield put(devicesActions.deleteDeviceSuccess());

		if (failed > 0) {
			Notification('warning', 'Some devices were not deleted', `${deleted} deleted, ${failed} failed.`);
		} else if (deleted === 0 && notFound > 0) {
			// Someone else already removed them; the refresh above drops the stale rows.
			Notification(
				'info',
				'Already deleted',
				notFound > 1 ? `${notFound} devices were already deleted.` : 'This device was already deleted.'
			);
		} else {
			Notification(
				'success',
				deleted > 1 ? 'Devices deleted' : 'Device deleted',
				deleted > 1 ? `${deleted} devices have been deleted.` : 'Device has been deleted.'
			);
		}
	} catch (error) {
		Notification('error', 'Delete failed', error.message || 'Delete failed. Please try again.');
		yield put(devicesActions.deleteDeviceError(error));
	}
}

//🔥 Actions here!, After deleting device
export function* deleteDeviceSuccess() {
	yield put(devicesActions.refreshDevices());
	// The server drops the device's delivery rows with no socket signal; refetch Credential Sync.
	yield put(credentialsActions.fetchPropagation());
}

// Fetch fields for a device — patches inside the active scope bucket
export function* getDeviceFields({ payload }) {
	try {
		const { orgID, siteID } = yield select((state) => state.Auth.selectedProfile);
		const { deviceID } = payload;
		const fields = yield call(() => SuperFetch.get(`/devices/${deviceID}/fields`));

		if (fields.statusCode === 200) {
			yield put(devicesActions.loadDeviceFieldsSuccess({ orgID, siteID, deviceID, fields: fields.data }));
		}
	} catch (error) {
		yield put(devicesActions.loadDeviceFieldsError(error));
	}
}

export function* createDeviceField({ payload }) {
	try {
		const { deviceID, data } = payload;
		const field = yield call(() => SuperFetch.post(`/devices/${deviceID}/fields`, data));
		if (field.statusCode === 201 || field.statusCode === 200) {
			yield put(devicesActions.createDeviceFieldSuccess({ deviceID }));
			Notification('success', 'Field created', 'Device field created successfully.');
		} else {
			throw Error(field.error?.message || field.error || 'Failed to create device field.');
		}
	} catch (error) {
		Notification('error', 'Field failed', error.message || 'Failed to create device field.');
		yield put(devicesActions.createDeviceFieldError(error));
	}
}

//🔥 Actions here!, After adding device field
export function* createDeviceFieldSuccess({ payload }) {
	const { deviceID } = payload;
	yield put(devicesActions.loadDeviceFields({ deviceID }));
}

export function* updateDeviceField({ payload }) {
	try {
		const { deviceID, data } = payload;
		const field = yield call(() => SuperFetch.post(`/devices/${deviceID}/fields`, data));
		if (field.statusCode === 201 || field.statusCode === 200) {
			yield put(devicesActions.updateDeviceFieldSuccess({ deviceID }));
			Notification('success', 'Field updated', 'Device field updated successfully.');
		} else {
			throw Error(field.error?.message || field.error || 'Failed to update device field.');
		}
	} catch (error) {
		Notification('error', 'Update failed', error.message || 'Failed to update device field.');
		yield put(devicesActions.updateDeviceFieldError(error));
	}
}

//🔥 Actions here!, After adding device field
export function* updateDeviceFieldSuccess({ payload }) {
	const { deviceID } = payload;
	yield put(devicesActions.loadDeviceFields({ deviceID }));
}

export function* deleteDeviceField({ payload }) {
	try {
		const { deviceID, fieldID } = payload;
		const field = yield call(() => SuperFetch.delete(`/devices/${deviceID}/fields/${fieldID}`));
		if (field.statusCode === 200) {
			Notification('success', 'Field deleted', 'Device field deleted successfully.');
			yield put(devicesActions.deleteDeviceFieldSuccess({ deviceID }));
		} else {
			throw Error(field.error?.message || field.error || 'Failed to delete device field.');
		}
	} catch (error) {
		Notification('error', 'Delete failed', error.message || 'Failed to delete device field.');
		yield put(devicesActions.deleteDeviceFieldError(error));
	}
}
//🔥 Actions here!, After deleting device field
export function* deleteDeviceFieldSuccess({ payload }) {
	const { deviceID } = payload;
	yield put(devicesActions.loadDeviceFields({ deviceID }));
}

// Fetch location for a device — patches inside the active scope bucket
export function* getDeviceLocation({ payload }) {
	try {
		const { orgID, siteID } = yield select((state) => state.Auth.selectedProfile);
		const { deviceID } = payload;
		const location = yield call(() => SuperFetch.get(`/devices/${deviceID}/location`));
		if (location.statusCode === 200) {
			yield put(devicesActions.loadDeviceLocationSuccess({ orgID, siteID, deviceID, location: location.data }));
		} else {
			throw Error(location.error.message || 'Failed to load device location.');
		}
	} catch (error) {
		yield put(devicesActions.loadDeviceLocationError(error));
	}
}

export function* createDeviceLocation({ payload }) {
	try {
		const { deviceID, data } = payload;
		const location = yield call(() => SuperFetch.post(`/devices/${deviceID}/location`, data));
		if (location.statusCode === 201 || location.statusCode === 200) {
			yield put(devicesActions.createDeviceLocationSuccess({ deviceID }));
			Notification('success', 'Location created', 'Device location created successfully.');
		} else {
			throw Error(location.error.message || 'Failed to create device location.');
		}
	} catch (error) {
		Notification('error', 'Location failed', error.message || 'Failed to create device location.');

		yield put(devicesActions.createDeviceLocationError(error));
	}
}

//🔥 Actions here!, After adding device location
export function* createDeviceLocationSuccess({ payload }) {
	const { deviceID } = payload;
	yield put(devicesActions.loadDeviceLocation({ deviceID }));
}

export function* updateDeviceLocation({ payload }) {
	try {
		const { deviceID, locationID, data } = payload;
		const location = yield call(() => SuperFetch.patch(`/devices/${deviceID}/location/${locationID}`, data));
		if (location.statusCode === 200) {
			Notification('success', 'Location updated', 'Device location updated successfully.');
			yield put(devicesActions.updateDeviceLocationSuccess({ deviceID }));
		} else {
			throw Error(location.error.message || 'Failed to update device location.');
		}
	} catch (error) {
		Notification('error', 'Update failed', error.message || 'Failed to update device location.');
		yield put(devicesActions.updateDeviceLocationError(error));
	}
}

//🔥 Actions here!, After updating device location
export function* updateDeviceLocationSuccess({ payload }) {
	const { deviceID } = payload;
	yield put(devicesActions.loadDeviceLocation({ deviceID }));
}

export function* deleteDeviceLocation({ payload }) {
	try {
		const { orgID, siteID } = yield select((state) => state.Auth.selectedProfile);
		const { deviceID, locationID } = payload;
		const location = yield call(() => SuperFetch.delete(`/devices/${deviceID}/location/${locationID}`));
		if (location.statusCode === 200) {
			yield put(devicesActions.deleteDeviceLocationSuccess({ orgID, siteID, deviceID }));
			Notification('success', 'Location deleted', 'Device removed successfully.');
		} else {
			Notification('error', 'Delete failed', 'Failed to delete device location.');
			throw Error(location.error.message || 'Failed to delete device location.');
		}
	} catch (error) {
		yield put(devicesActions.deleteDeviceLocationError(error));
	}
}

//🔥 Actions here!, After deleting device location
export function* deleteDeviceLocationSuccess() {
	yield put(devicesActions.loadAllDevicesLocations());
}

// ✅ Move Device Location (single upsert — backend handles site association + old location cleanup)
export function* upsertDeviceLocation({ payload }) {
	try {
		const { deviceID, newLocation } = payload;
		const { floorID, siteID, xAxis, yAxis, mapID } = newLocation;

		const result = yield call(() =>
			SuperFetch.post(`/devices/${deviceID}/location`, { siteID, floorID, mapID, xAxis, yAxis })
		);

		if (result.statusCode === 201 || result.statusCode === 200) {
			yield put(devicesActions.upsertDeviceLocationSuccess());
			Notification('success', 'Location created', 'Device location updated successfully.');
		} else {
			throw Error(result.error?.message || 'Failed to create device location.');
		}
	} catch (error) {
		Notification('error', 'Location failed', error.message || 'Failed to move device location.');
		yield put(devicesActions.upsertDeviceLocationError(error));
	}
}

export function* upsertDeviceLocationSuccess() {
	yield put(devicesActions.loadAllDevicesLocations());
}

//✅ Device Sites
export function* updateDeviceSites({ payload }) {
	try {
		const { deviceID, siteIDs } = payload;
		const result = yield call(() => SuperFetch.put(`/devices/${deviceID}/sites`, { siteIDs }));
		if (result.statusCode === 200) {
			yield put(devicesActions.updateDeviceSitesSuccess());
			Notification('success', 'Sites updated', 'Device sites updated successfully.');
			yield put(devicesActions.loadAllDevicesLocations());
		} else {
			throw Error(result.error?.message || 'Failed to update device sites.');
		}
	} catch (error) {
		Notification('error', 'Update failed', error.message || 'Failed to update device sites.');
		yield put(devicesActions.updateDeviceSitesError(error));
	}
}

//✅ Devices Helpers
export function* dispatchDevicesAction({ payload }) {
	const { deviceIDs, categoryIDs, actionID, platform, method } = payload;

	try {
		const data = { deviceIDs, categoryIDs, actionID, platform, method };
		const action = yield call(() => SuperFetch.post('/devices/action', data));
		if (action.statusCode === 200) {
			yield put(devicesActions.actionDevicesSuccess());
			Notification('success', 'Action sent', 'Unlock Lock(s) successfully.');
		} else {
			Notification('error', 'Action failed', 'Failed to unlock lock(s).');
			throw Error(action.error.message || 'Failed to dispatch devices action.');
		}
	} catch (error) {
		yield put(devicesActions.actionDevicesError(error));
	}
}

/**
 * Resolve the doors an override is about to action. Groups are expanded HERE, on the client, because
 * `/devices/override` is device-addressed: it actions one door per request and reports that door's
 * outcome, which is the only way the operator learns which locks opened.
 *
 * `effective=true` matters — the plain read returns a group's DIRECT members only, so a nested group
 * would silently drop its child groups' locks.
 */
function* resolveOverrideTargets({ deviceIDs, groupIDs }) {
	const ids = new Set(deviceIDs.map(Number));

	for (const groupID of groupIDs) {
		const res = yield call(() => SuperFetch.get(`/deviceGroups/${groupID}/assets`, {}, { effective: true }));
		if (res.code !== 200 && res.statusCode !== 200) {
			throw Error(describeUnlockError(res));
		}
		for (const id of res.data || []) ids.add(Number(id));
	}
	return [...ids];
}

/**
 * Override (admin lock/unlock) — ONE `/devices/override` request per device, issued one at a time.
 * actionID 1 = unlock, 2 = lock. Shared by ControlDeviceModal and ControlEmergencyModal.
 *
 * Why per device rather than one request for the batch: actuation is serial and every door is a vendor
 * round trip, so a 40-lock group in a single request runs long enough that the response never reaches
 * the browser — the operator learns nothing. One request per door returns that door's verdict as soon
 * as it is known, so the modal fills in live and a failure can be retried on its own.
 *
 * `payload.retry` re-arms only the named devices and leaves the other rows' verdicts standing.
 */
export function* overrideDevice({ payload }) {
	const { deviceIDs = [], groupIDs = [], actionID, retry = false } = payload;
	const verb = actionID === 2 ? 'Lock' : 'Unlock';

	try {
		const targets = yield call(resolveOverrideTargets, { deviceIDs, groupIDs });

		if (!targets.length) {
			Notification('warning', `Nothing to ${verb.toLowerCase()}`, 'That selection has no actionable devices.');
			yield put(devicesActions.overrideDeviceSuccess());
			return;
		}

		// Name the rows from the store so the list reads as devices, not ids. A device missing from the
		// active scope still gets a row — it is being actioned either way.
		const known = yield select(selectDevices);
		const nameOf = new Map(known.map((d) => [Number(d.deviceID), d.name]));
		yield put(
			devicesActions.overrideDeviceTargets({
				actionID,
				merge: retry,
				rows: targets.map((deviceID) => ({
					deviceID,
					name: nameOf.get(deviceID) ?? `Device ${deviceID}`,
					status: 'pending',
					reason: null
				}))
			})
		);

		// ONE DOOR AT A TIME, deliberately. Seven devices means seven requests, in order, each waiting
		// for the one before it.
		//
		// Why not concurrently: our own dispatch layer states that a gateway handles one action at a
		// time, and this path never learns which gateway a lock is behind (no lock→gateway mapping in
		// `getDevices` or in the server's manage helpers). Sending several at once would therefore break
		// that rule blindly whenever locks share a gateway, and the symptom would look like a failing
		// lock. Sequential is the only shape that cannot.
		//
		// It costs wall clock and nothing else: each door is its own request, so a long run still reports
		// every verdict — there is no response left to lose. If concurrency is ever worth measuring for,
		// it has to be keyed per gateway, not a global width, so no pool constant is left here to raise.
		const settled = [];
		for (const deviceID of targets) {
			const row = yield call(overrideOneDevice, { deviceID, actionID });
			settled.push(row);
			yield put(devicesActions.overrideDeviceRow(row));
		}

		const { type, title, description } = buildOverrideToast({ action: verb, results: settled });
		Notification(type, title, description);
		yield put(devicesActions.overrideDeviceSuccess());

		yield call(refreshActivityAfterAction);
	} catch (error) {
		Notification('error', `${verb} failed`, error.message || UNLOCK_UNREACHABLE_MESSAGE);
		yield put(devicesActions.overrideDeviceError(error.message));
	}
}

/**
 * Pull the access log again after a lock/unlock, so the operator sees the command they just issued.
 *
 * Our own row is already written and tagged with the client that sent it (Platform 'dashboard' →
 * "Cloud Access"), and the server writes each door's row BEFORE answering that door's request — so by
 * the time the run finishes every row exists. Nothing here races the writes.
 *
 * BOTH feeds, because they are different views of the same event and a stale one reads as "it didn't
 * work": the entity drawer's Activity tab (a no-op when no drawer is open) and the portfolio access-log
 * card, whose own refresh rules are reused rather than re-derived. Today's counts come with the card —
 * `app/saga` loads the two together, and an unlock moves both.
 */
export function* refreshActivityAfterAction() {
	yield call(refreshHeldActivity, devicesActions, 'Devices');

	const { orgID } = yield select((state) => state.Auth.selectedProfile);
	const filters = yield select((state) => state.AccessLogs.filters[orgID]);
	yield put(accessLogRefreshAction({ orgID, filters }));
	yield put(accessLogsActions.loadTodayCounts());
}

/**
 * Action ONE door and turn the response into a progress row. Never throws: a door that fails is a
 * row, not an aborted batch — the other doors in the fan-out must still be actioned and reported.
 *
 * The server answers with `{ succeeded, failed, skipped, results: [ { success, retryable, reason } ] }`
 * for the single device, so the row is read straight off `results[0]`.
 */
export function* overrideOneDevice({ deviceID, actionID }) {
	try {
		const res = yield call(() => SuperFetch.post('/devices/override', { deviceIDs: [deviceID], actionID }));
		return overrideRowFromResponse(deviceID, res);
	} catch (error) {
		return { deviceID, status: 'failed', reason: error.message || UNLOCK_UNREACHABLE_MESSAGE };
	}
}

// Live check for one device (POST /devices/:deviceID/check-live); the server persists status + battery.
// Both verdicts toast (offline changes no badge, so silence looks broken); `quiet` mutes bulk checks.
export function* checkDeviceLive({ payload }) {
	const { deviceID, quiet = false } = payload;
	try {
		const res = yield call(() => SuperFetch.post(`/devices/${deviceID}/check-live`));
		if (res.statusCode !== 200) {
			// Read `error` first: it carries the route's reason (e.g. TTLock has no live check).
			const detail = res.error?.message || (typeof res.error === 'string' && res.error) || res.errors || res.info;
			throw Error(detail || 'Live check failed.');
		}
		const live = res.data || {};
		yield put(devicesActions.checkDeviceLiveSuccess({ deviceID, ...live }));
		yield* refreshCheckedDevice(deviceID);
		if (!quiet) {
			const known = yield select(selectDevices);
			const deviceName = known.find((d) => Number(d.deviceID) === Number(deviceID))?.name || 'Device';
			if (live.online === true) {
				const batteryNote = live.battery != null ? ` · battery ${live.battery}%` : '';
				Notification('success', `${deviceName} is online`, `Answered the live check${batteryNote}.`);
			} else if (live.online === false) {
				Notification('warning', `${deviceName} did not respond`, 'No answer to the live check.');
			}
		}
	} catch (error) {
		if (!quiet) Notification('error', 'Device check failed', error.message || 'Try again shortly.');
		yield put(devicesActions.checkDeviceLiveError({ deviceID }));
	}
}

/**
 * Everything a finished check made stale, re-read for the device that was checked.
 *
 * A check writes TWO things server-side and returns NEITHER of them (ConnectivityStateWriter):
 * the device's custom fields, which hold its CURRENT status + battery, and a heartbeat row,
 * which is the HISTORY. Without this the operator watches a check succeed and then reads the
 * status from before it, above a feed that never mentions the check they just ran.
 *
 * The fields are always re-read — every device surface renders the pair promoted out of them.
 * The history only where this session has already asked for it: an unopened feed fetches on
 * mount anyway, so a bulk check must not pull a timeline per device nobody is looking at.
 */
export function* refreshCheckedDevice(deviceID) {
	yield put(devicesActions.loadDeviceFields({ deviceID }));
	const history = yield select((state) => state.Devices.historyByDeviceID?.[deviceID]);
	if (history?.recordsRequested) yield put(devicesActions.loadDeviceHistory({ deviceID, limit: history.limit }));
}

/**
 * Connectivity history for ONE device — GET /devices/:deviceID/history.
 *
 * One request, both resolutions: `buckets` (the per-day roll-up) and `records`/`nextBefore`
 * (the check timeline). Our own heartbeat table, so it never touches a vendor.
 *
 * Quiet on failure: this is ambient context in a drawer, not an action someone invoked.
 *
 * @param {Object} payload - { deviceID, days, limit, before }
 */
export function* loadDeviceHistory({ payload }) {
	const { deviceID, days = 5, limit = 20, before = null } = payload;
	try {
		const query = before ? { days, limit, before } : { days, limit };
		const res = yield call(() => SuperFetch.get(`/devices/${deviceID}/history`, {}, query));
		if (res.statusCode !== 200) throw Error('Could not load connectivity history.');
		yield put(
			devicesActions.loadDeviceHistorySuccess({
				deviceID,
				buckets: res.data?.buckets || [],
				records: res.data?.records || [],
				nextBefore: res.data?.nextBefore ?? null,
				append: !!before
			})
		);
	} catch (error) {
		yield put(devicesActions.loadDeviceHistoryError({ deviceID, error: error.message }));
	}
}

export default function* devicesSaga() {
	//✅ Devices
	yield takeLatest(devicesActions.loadDevices, getDevices);
	yield takeLatest(devicesActions.refreshDevices, refreshDevices);
	yield takeLatest(devicesActions.loadDevicesSuccess, getDevicesSuccess);
	yield takeLatest(devicesActions.refreshDevicesSuccess, getDevicesSuccess);
	yield takeLatest(devicesActions.loadAllDevicesLocations, getAllDevicesLocations);
	yield takeLatest(devicesActions.createDevice, createDevice);
	yield takeLatest(devicesActions.createDeviceSuccess, createDeviceSuccess);
	yield takeLatest(devicesActions.createMultipleDevices, createMultipleDevices);
	yield takeLatest(devicesActions.createMultipleDevicesSuccess, createMultipleDevicesSuccess);
	yield takeLatest(devicesActions.updateDevice, updateDevice);
	yield takeLatest(devicesActions.updateDeviceSuccess, updateDeviceSuccess);
	yield takeLatest(devicesActions.deleteDevice, deleteDevice);
	yield takeLatest(devicesActions.deleteDeviceSuccess, deleteDeviceSuccess);

	//✅  Devices Fields
	// takeEvery, not takeLatest: a BULK live check refreshes the fields of every device it
	// checked, and takeLatest cancelled all but the last — nineteen locks left showing the
	// status they had before the check, which is worse than not refreshing at all. One read per
	// device, and each one patches only its own row.
	yield takeEvery(devicesActions.loadDeviceFields, getDeviceFields);
	yield takeLatest(devicesActions.createDeviceField, createDeviceField);
	yield takeLatest(devicesActions.createDeviceFieldSuccess, createDeviceFieldSuccess);
	// takeEVERY, for the same reason as the read above: the drawer's hardware rows save one
	// field at a time, so filling in a serial and then a model dispatches twice in quick
	// succession. Under takeLatest the first write is cancelled mid-flight and the value the
	// user typed is silently lost — they watch it revert.
	yield takeEvery(devicesActions.updateDeviceField, updateDeviceField);
	yield takeEvery(devicesActions.updateDeviceFieldSuccess, updateDeviceFieldSuccess);
	yield takeLatest(devicesActions.deleteDeviceField, deleteDeviceField);
	yield takeLatest(devicesActions.deleteDeviceFieldSuccess, deleteDeviceFieldSuccess);

	//✅  Devices Location
	yield takeLatest(devicesActions.loadDeviceLocation, getDeviceLocation);
	yield takeLatest(devicesActions.createDeviceLocation, createDeviceLocation);
	yield takeLatest(devicesActions.createDeviceLocationSuccess, createDeviceLocationSuccess);
	yield takeLatest(devicesActions.updateDeviceLocation, updateDeviceLocation);
	yield takeLatest(devicesActions.updateDeviceLocationSuccess, updateDeviceLocationSuccess);
	yield takeLatest(devicesActions.deleteDeviceLocation, deleteDeviceLocation);
	yield takeLatest(devicesActions.deleteDeviceLocationSuccess, deleteDeviceLocationSuccess);

	//✅  Move Device Location (compound)
	yield takeLatest(devicesActions.upsertDeviceLocation, upsertDeviceLocation);
	yield takeLatest(devicesActions.upsertDeviceLocationSuccess, upsertDeviceLocationSuccess);

	//✅  Device Sites
	yield takeLatest(devicesActions.updateDeviceSites, updateDeviceSites);

	//✅  Devices Actions
	yield takeLatest(devicesActions.actionDevices, dispatchDevicesAction);

	//✅  Override (admin lock/unlock) — takeEvery so a device + group override can run concurrently
	yield takeEvery(devicesActions.overrideDevice, overrideDevice);
	yield takeLatest(devicesActions.activityStale, refreshActivityAfterAction);

	//✅  Live check — takeEvery so two devices can be checked at once; neither request
	// may cancel the other, they are different radios.
	yield takeEvery(devicesActions.checkDeviceLive, checkDeviceLive);

	//✅  Connectivity history — takeEvery so two drawers can load their own.
	yield takeEvery(devicesActions.loadDeviceHistory, loadDeviceHistory);

	yield* watchActivity(devicesActions);
}
